import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SuperAdminNav from '../../components/SuperAdminNav';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatRelativeDate(dateStr) {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatScheduleDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatScheduleTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function ordinalSuffix(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function getScheduleDisplay(email) {
  const { type, day_number, frequency, day_of_week, day_of_month, trigger_name, scheduled_date, scheduled_time, send_mode, status } = email;

  if (type === 'onboarding') {
    return `Day ${day_number}`;
  }

  if (type === 'timed') {
    if (frequency === 'monthly') {
      return `${ordinalSuffix(day_of_month || 1)} of month`;
    }
    if (frequency === 'biweekly') {
      return `Every other ${DAY_NAMES[day_of_week] || ''}`;
    }
    // weekly
    return `${DAY_NAMES[day_of_week] || ''}s`;
  }

  if (type === 'triggered') {
    return `Trigger: ${trigger_name || ''}`;
  }

  if (type === 'broadcast') {
    if (status === 'sent') {
      return 'Sent';
    }
    if (send_mode === 'schedule' && scheduled_date) {
      return `${scheduled_date} at ${scheduled_time || '08:00'} local`;
    }
    if (send_mode === 'send_now') {
      return 'Send now';
    }
    return '--';
  }

  return '--';
}

function StatusBadge({ status }) {
  const colors = {
    active: '#22c55e',
    paused: '#eab308',
    scheduled: '#3b82f6',
    sent: '#6b7280',
    archived: '#6b7280',
    draft: '#6b7280',
  };
  const bg = colors[status] || '#6b7280';

  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: '0.75rem',
      fontWeight: 600,
      color: '#fff',
      background: bg,
      textTransform: 'capitalize',
      whiteSpace: 'nowrap',
    }}>
      {status}
    </span>
  );
}

function getSortValue(email, column) {
  switch (column) {
    case 'internal_name': return (email.internal_name || '').toLowerCase();
    case 'type': return email.type || '';
    case 'recipient': return email.recipient_type || '';
    case 'schedule': return getScheduleDisplay(email);
    case 'send_time': return email.send_time || '';
    case 'status': return email.status || '';
    case 'sent_7d': return email.sent_7d || 0;
    case 'open_count': return email.open_count || 0;
    case 'click_count': return email.click_count || 0;
    case 'updated_at': return email.updated_at || '';
    default: return '';
  }
}

export default function SuperEmails() {
  const { apiFetch } = useAuth();
  const navigate = useNavigate();

  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [pauseAll, setPauseAll] = useState(false);
  const [pauseAllLoading, setPauseAllLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const [actionLoading, setActionLoading] = useState('');

  const loadEmails = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/super/emails?archived=${showArchived}`);
      setEmails(data.emails);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, showArchived]);

  const loadPauseAll = useCallback(async () => {
    try {
      const data = await apiFetch('/api/super/email-pause-all');
      setPauseAll(data.paused);
    } catch (err) {
      console.error(err);
    }
  }, [apiFetch]);

  useEffect(() => { loadEmails(); }, [loadEmails]);
  useEffect(() => { loadPauseAll(); }, [loadPauseAll]);

  const handlePauseAll = async () => {
    setPauseAllLoading(true);
    try {
      const data = await apiFetch('/api/super/email-pause-all', {
        method: 'POST',
        body: JSON.stringify({ paused: !pauseAll }),
      });
      setPauseAll(data.paused);
    } catch (err) {
      console.error(err);
    } finally {
      setPauseAllLoading(false);
    }
  };

  const handlePause = async (id) => {
    setActionLoading(`pause-${id}`);
    try {
      const data = await apiFetch(`/api/super/emails/${id}/pause`, { method: 'POST' });
      setEmails(prev => prev.map(e => e.id === id ? data.email : e));
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading('');
    }
  };

  const handleArchive = async (id) => {
    setActionLoading(`archive-${id}`);
    try {
      const data = await apiFetch(`/api/super/emails/${id}/archive`, { method: 'POST' });
      setEmails(prev => prev.map(e => e.id === id ? data.email : e));
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading('');
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDir('asc');
    }
  };

  const sorted = [...emails].sort((a, b) => {
    const aVal = getSortValue(a, sortColumn);
    const bVal = getSortValue(b, sortColumn);
    let cmp = 0;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      cmp = aVal - bVal;
    } else {
      cmp = String(aVal).localeCompare(String(bVal));
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const canPause = (email) => email.type === 'timed' || email.type === 'triggered';
  const canArchive = (email) => {
    if (email.type === 'triggered') return false;
    if (email.type === 'broadcast' && email.status === 'sent') return false;
    return email.type === 'onboarding' || email.type === 'timed' || email.type === 'broadcast';
  };

  const SortHeader = ({ column, children }) => {
    const active = sortColumn === column;
    return (
      <th
        onClick={() => handleSort(column)}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      >
        {children}
        {active && (
          <span style={{ marginLeft: 4, fontSize: '0.7rem' }}>
            {sortDir === 'asc' ? '\u25B2' : '\u25BC'}
          </span>
        )}
      </th>
    );
  };

  return (
    <div className="admin-layout">
      <SuperAdminNav activeItem="emails" />
      <div className="admin-page" style={{ maxWidth: 1400 }}>

        {/* Pause-all banner */}
        {pauseAll && (
          <div style={{
            background: '#b91c1c',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: 8,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <span style={{ fontWeight: 600 }}>All timed and triggered emails are paused</span>
            <button
              className="btn btn-sm"
              style={{ background: '#fff', color: '#b91c1c', fontWeight: 600 }}
              onClick={handlePauseAll}
              disabled={pauseAllLoading}
            >
              {pauseAllLoading ? '...' : 'Resume'}
            </button>
          </div>
        )}

        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn"
              style={{ background: '#f77c00', color: '#fff' }}
              onClick={() => navigate('/super/emails/new/onboarding')}
            >
              New Onboarding Email
            </button>
            <button
              className="btn"
              style={{ background: '#f77c00', color: '#fff' }}
              onClick={() => navigate('/super/emails/new/timed')}
            >
              New Timed Email
            </button>
            <button
              className="btn"
              style={{ background: '#f77c00', color: '#fff' }}
              onClick={() => navigate('/super/emails/new/broadcast')}
            >
              New Broadcast Email
            </button>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              className="btn btn-sm"
              style={{
                background: pauseAll ? '#b91c1c' : '#4b5563',
                color: '#fff',
                fontWeight: 600,
              }}
              onClick={handlePauseAll}
              disabled={pauseAllLoading}
            >
              {pauseAllLoading ? '...' : pauseAll ? 'Pause All (ON)' : 'Pause All'}
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', fontSize: '0.85rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={e => { setShowArchived(e.target.checked); setLoading(true); }}
              />
              Show archived
            </label>
          </div>
        </div>

        {/* Back link */}
        <div style={{ marginBottom: 16 }}>
          <button
            className="btn btn-sm btn-outline"
            onClick={() => navigate('/super')}
          >
            &larr; Back to Super Admin
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : emails.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>
            No emails found.
          </div>
        ) : (
          <div className="super-table-wrap">
            <table className="super-table">
              <thead>
                <tr>
                  <SortHeader column="internal_name">Name</SortHeader>
                  <SortHeader column="type">Type</SortHeader>
                  <SortHeader column="recipient">Recipient</SortHeader>
                  <SortHeader column="schedule">Schedule</SortHeader>
                  <SortHeader column="send_time">Send Time</SortHeader>
                  <SortHeader column="status">Status</SortHeader>
                  <SortHeader column="sent_7d">Sent 7d</SortHeader>
                  <SortHeader column="open_count">Opens</SortHeader>
                  <SortHeader column="click_count">Clicks</SortHeader>
                  <SortHeader column="updated_at">Last Edited</SortHeader>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(email => (
                  <tr key={email.id} className="super-table-row">
                    <td style={{ fontWeight: 600, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {email.internal_name}
                    </td>
                    <td style={{ textTransform: 'capitalize' }}>{email.type}</td>
                    <td>{{ coach: 'Coach', club_pp: 'Club (Parent Pay)', club_cp: 'Club (Club Pays)', parent_cp: 'Parent (Club Pay)', parent_pp: 'Parent (Parent Pay)', parent_ind: 'Parent (Ind/Family)' }[email.recipient_type] || email.recipient_type}</td>
                    <td style={{ fontSize: '0.85rem' }}>{getScheduleDisplay(email)}</td>
                    <td style={{ fontSize: '0.85rem' }}>{email.send_time || '--'}</td>
                    <td><StatusBadge status={email.status} /></td>
                    <td>{email.sent_7d ?? 0}</td>
                    <td>{email.open_count ?? 0}</td>
                    <td>{email.click_count ?? 0}</td>
                    <td style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{formatRelativeDate(email.updated_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => navigate(`/super/emails/${email.id}`)}
                        >
                          Edit
                        </button>
                        {canPause(email) && (
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handlePause(email.id)}
                            disabled={actionLoading === `pause-${email.id}`}
                          >
                            {actionLoading === `pause-${email.id}` ? '...' : email.status === 'paused' ? 'Resume' : 'Pause'}
                          </button>
                        )}
                        {canArchive(email) && (
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handleArchive(email.id)}
                            disabled={actionLoading === `archive-${email.id}`}
                            style={{ color: '#6b7280' }}
                          >
                            {actionLoading === `archive-${email.id}` ? '...' : 'Archive'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
